import type {
  CompanyElementMapping,
  ComponentParameter,
  CsvPreview,
} from "../types";
import {
  END_DATE_HEADER_LC,
  START_DATE_HEADER_LC,
  resolveDateColumnHeader,
} from "./dateColumns";

/** Reserved / informational columns that are never component parameter values. */
export const RESERVED_LC = new Set([
  "employee code",
  "component name",
  "deduction name",
  "employee name",
  "start date",
  "end date",
  "row mode",
]);

/** A single mapped column value resolved to the parameter it will write to. */
export interface MappedValue {
  /** The source CSV column header. */
  column: string;
  /** The raw value from the CSV cell. */
  value: string;
  /** The resolved Christmas parameter display name. */
  parameter: string;
  /** The resolved bucket type id, or null when the parameter could not be resolved. */
  bucketTypeId: number | null;
  /** Whether the resolved parameter is sealed. */
  sealed: boolean;
}

/** One CSV row with all of its resolved parameter mappings. */
export interface MappedRow {
  /** 1-based row number matching the CSV. */
  index: number;
  employeeCode: string;
  employeeName: string;
  component: string;
  /** ISO start date from the mapped start-date column, when present. */
  startDate?: string;
  /** ISO end date from the mapped end-date column, when present. */
  endDate?: string;
  values: MappedValue[];
}

/** Resolve a saved mapping to the live parameter (by qualified name, else display name). */
export function resolveParam(
  params: ComponentParameter[],
  mapping: CompanyElementMapping,
): ComponentParameter | undefined {
  if (mapping.parameterQualifiedName !== null) {
    return params.find(
      (p) => p.parameterQualifiedName === mapping.parameterQualifiedName,
    );
  }
  return params.find(
    (p) =>
      p.parameterQualifiedName === null &&
      p.displayName === mapping.parameterDisplayName,
  );
}

/**
 * Builds the fully-mapped view of a CSV import: every row with the parameter
 * each mapped column resolves to. Shared by the Review screen (display) and the
 * results export (download) so both reflect the same mapped data.
 */
export function buildMappedRows(args: {
  preview: CsvPreview | null;
  componentColumn: string;
  /** Override for the start-date CSV header; falls back to reserved `Start Date`. */
  startDateColumn?: string | null;
  /** Override for the end-date CSV header; falls back to reserved `End Date`. */
  endDateColumn?: string | null;
  paramsByComponent: Record<string, ComponentParameter[]>;
  mappings: CompanyElementMapping[];
}): MappedRow[] {
  const {
    preview,
    componentColumn,
    startDateColumn: startDateColumnOverride,
    endDateColumn: endDateColumnOverride,
    paramsByComponent,
    mappings,
  } = args;
  if (!preview) return [];

  const headers = preview.headers;
  const rows = preview.rows;

  const employeeCodeColumn =
    headers.find((h) => h.toLowerCase() === "employee code") ?? "";
  const employeeNameColumn =
    headers.find((h) => h.toLowerCase() === "employee name") ?? "";
  const startDateColumn =
    resolveDateColumnHeader(
      headers,
      startDateColumnOverride,
      START_DATE_HEADER_LC,
    ) ?? "";
  const endDateColumn =
    resolveDateColumnHeader(
      headers,
      endDateColumnOverride,
      END_DATE_HEADER_LC,
    ) ?? "";
  const dataColumns = headers.filter(
    (h) =>
      !RESERVED_LC.has(h.toLowerCase()) &&
      h !== componentColumn &&
      h !== startDateColumn &&
      h !== endDateColumn,
  );

  return rows.map((row, index) => {
    const component = (row[componentColumn] ?? "").trim();
    const params = paramsByComponent[component] ?? [];
    const element = params[0]?.elementQualifiedName;
    const values: MappedValue[] = [];
    if (element) {
      for (const col of dataColumns) {
        if (!(col in row)) continue;
        const mapping =
          mappings.find(
            (m) =>
              m.componentName === component &&
              m.elementQualifiedName === element &&
              m.columnName === col,
          ) ??
          mappings.find(
            (m) =>
              !m.componentName &&
              m.elementQualifiedName === element &&
              m.columnName === col,
          );
        if (!mapping) continue;
        const param = resolveParam(params, mapping);
        values.push({
          column: col,
          value: row[col] ?? "",
          parameter: param?.displayName ?? mapping.parameterDisplayName,
          bucketTypeId: param?.bucketTypeId ?? null,
          sealed: param?.isSealed ?? false,
        });
      }
    }
    const startDate = startDateColumn
      ? (row[startDateColumn] ?? "").trim()
      : "";
    const endDate = endDateColumn ? (row[endDateColumn] ?? "").trim() : "";
    return {
      index: index + 1,
      employeeCode: employeeCodeColumn ? (row[employeeCodeColumn] ?? "") : "",
      employeeName: employeeNameColumn ? (row[employeeNameColumn] ?? "") : "",
      component,
      ...(startDate !== "" ? { startDate } : {}),
      ...(endDate !== "" ? { endDate } : {}),
      values,
    };
  });
}
